import React, { ReactNode, useState } from 'react'
import { limitObj_12_24_48 } from 'shared/route-query/constants/pagination-limit-array'
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination'
import EmptyList from 'modules/profile/manage-subscriptions/components/table/empty-list'
import TableLimitPagination from 'modules/profile/manage-subscriptions/components/table/table-limit-pagination'
import { SchoolEnrollmentsApiInterface } from '../types/school-enrollments-interface'
import { getCourseCardLinkTarget } from '../utils/get-course-card-link-target'
import { SchoolCard } from './school-card/school-card'
import { SchoolCardsSkeleton } from './school-card/school-cards-skeleton'
import SchoolCourseBundleModal, {
  SchoolCourseBundleModalModalProps,
} from './school-course-bundle-modal'

export interface EnrollmentsTabProps extends Pick<
  ReturnType<typeof useFilterPagination>,
  'limit' | 'setLimit' | 'pagination' | 'setPagination'
> {
  enrollments: SchoolEnrollmentsApiInterface | undefined
  filterComponent: React.ReactNode
}

export const EnrollmentsTab = ({
  enrollments,
  limit,
  setLimit,
  pagination,
  setPagination,
  filterComponent,
}: EnrollmentsTabProps): ReactNode => {
  const [courseBundleModalState, setCourseBundleModalState] = useState<
    Omit<SchoolCourseBundleModalModalProps, 'onClose'>
  >({
    isOpened: false,
    schoolEnrollmentId: null,
    name: null,
    imagePath: null,
  })

  return (
    <div>
      {filterComponent}
      <div className="mt-9 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3 lg:gap-6 xl:grid-cols-4">
        {enrollments ? (
          enrollments.items.length > 0 ? (
            enrollments.items.map(courseItem => (
              <SchoolCard
                key={courseItem.id}
                variant={courseItem.type === 'bundle' ? 'dark' : 'white'}
                courseCover={courseItem.data.image?.path || null}
                description={courseItem.data.description}
                href={courseItem.type === 'course' ? courseItem.data.resumeUrl : null}
                target={
                  courseItem.type === 'course'
                    ? getCourseCardLinkTarget(courseItem.data.resumeUrl)
                    : null
                }
                instructorName={courseItem.data.instructorName}
                title={courseItem.data.name}
                onClick={
                  courseItem.type === 'bundle'
                    ? () =>
                        setCourseBundleModalState({
                          isOpened: true,
                          schoolEnrollmentId: courseItem.id,
                          name: courseItem.data.name,
                          imagePath: courseItem.data.image?.path || null,
                        })
                    : undefined
                }
                accessFrom={courseItem.type === 'course' ? courseItem.data.accessFrom : null}
                progress={courseItem.data.progress}
                {...(courseItem.type === 'bundle'
                  ? {
                      totalCoursesCount: courseItem.data.totalCoursesCount,
                      completedCoursesCount: courseItem.data.completedCoursesCount,
                    }
                  : {
                      totalLecturesCount: courseItem.data.totalLecturesCount,
                      completedLecturesCount: courseItem.data.completedLecturesCount,
                    })}
              />
            ))
          ) : (
            <EmptyList className="col-span-full" />
          )
        ) : (
          <SchoolCardsSkeleton />
        )}
      </div>
      <div className="mt-4 flex justify-end gap-6 md:mt-5 lg:mt-6">
        <TableLimitPagination
          data={enrollments}
          limiter={{ limit, setLimit }}
          paginator={{
            pagination,
            setPagination,
            hasMore: enrollments?.hasMore,
          }}
          limitObj={limitObj_12_24_48}
        />
      </div>
      <SchoolCourseBundleModal
        onClose={() =>
          setCourseBundleModalState({
            isOpened: false,
            schoolEnrollmentId: null,
            name: null,
            imagePath: null,
          })
        }
        {...courseBundleModalState}
      />
    </div>
  )
}
